import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Checkbox,
  Collapse,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Select,
  Spinner,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MdChevronRight, MdExpandMore } from "react-icons/md";
import { compilePrompt } from "../promptCompiler";
import useDeploy from "../useDeploy";
import useTools from "../../Tools/useTools";
import useTwilioAccounts from "../../Widgets/useTwilioAccounts";

const LLM_OPTIONS = [
  { value: "gpt-5.4-mini", label: "gpt-5.4-mini (default, cheapest)" },
  { value: "gpt-4o-mini", label: "gpt-4o-mini" },
  { value: "gpt-4o", label: "gpt-4o (10× cost)" },
  { value: "deepseek-chat", label: "deepseek-chat" },
  { value: "o3-mini", label: "o3-mini (2× cost, slower, smarter)" },
];

export default function Step6ReviewDeploy({ draft, onDeployed }) {
  const promptDisclosure = useDisclosure({ defaultIsOpen: false });
  const advancedDisclosure = useDisclosure({ defaultIsOpen: false });
  const isVoice = draft.channel === "voice";

  const [editedPrompt, setEditedPrompt] = useState(null);
  // Pick a short, name-shaped default. Prefer any field whose key looks
  // like a business name; fall back to "<Industry> agent" so the name
  // is always a recognisable short label, never a sentence of prose.
  const [assistantName, setAssistantName] = useState(() => {
    const fv = draft.fieldValues || {};
    const businessNameKey = (draft.config?.fields || []).find((f) =>
      /(practice|business|restaurant|brokerage|store|company|product)_name$/.test(f.key)
    )?.key;
    const fromField = businessNameKey ? fv[businessNameKey] : null;
    const fromIndustry = draft.config?.industry_tag
      ? `${draft.config.industry_tag.split(/[/·]/)[0].trim()} agent`
      : null;
    const candidate = (fromField || fromIndustry || "My agent").toString().trim();
    return candidate.slice(0, 60);
  });

  // Advanced overrides — kept in local state until Deploy so they
  // don't pollute the wizard draft. Voice + realtime is the only
  // combination that triggers the assistant_type swap.
  const [llmModel, setLlmModel] = useState("gpt-5.4-mini");
  const [useRealtime, setUseRealtime] = useState(false);

  // Twilio picker state — voice channel only.
  const [twilioAuthId, setTwilioAuthId] = useState("");
  const [phoneSid, setPhoneSid] = useState("");
  const { accounts, phoneNumbers } = useTwilioAccounts({
    widgetType: isVoice ? "phone" : null,
    twilio_auth_id: twilioAuthId,
  });

  const { toolsList } = useTools();
  const { deploy, deploying, progress } = useDeploy();

  const compiledPrompt = useMemo(
    () =>
      editedPrompt ??
      compilePrompt(
        draft.config?.prompt_skeleton || "",
        draft.fieldValues,
        draft.config?.fields || []
      ),
    [editedPrompt, draft.config, draft.fieldValues]
  );

  const selectedPhone = phoneNumbers.find((p) => p.sid === phoneSid);

  const canDeploy =
    !!assistantName.trim() &&
    (!isVoice || (twilioAuthId && phoneSid));

  const onClickDeploy = async () => {
    try {
      const result = await deploy({
        draft,
        compiledPrompt,
        assistantName,
        toolsList,
        twilio: isVoice
          ? {
              twilio_auth_id: twilioAuthId,
              sid: phoneSid,
              phone_number: selectedPhone?.friendly_name,
            }
          : null,
        advanced: { llmModel, realtime: useRealtime },
      });
      // Fatal-deploy errors (no assistant created) stay on Step 6 with
      // a toast; partial failures still move to Step 7 so the user
      // sees what worked + can finish the rest manually.
      if (result?.fatal) {
        toast.error(result.errors?.[0] || "Deploy failed");
        return;
      }
      onDeployed(result);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Deploy failed — see console for details"
      );
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      <VStack spacing={1} textAlign="center">
        <Heading size="lg">Review & deploy</Heading>
        <Text color="gray.600">
          Take a last look. You can edit the prompt or anything else from the
          Assistants page after deploy.
        </Text>
      </VStack>

      <Card>
        <CardBody>
          <VStack spacing={3} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="semibold">Assistant name</Text>
              <Text color="gray.500" fontSize="sm">
                {isVoice
                  ? useRealtime
                    ? "Voice (Realtime)"
                    : "Voice (phone)"
                  : "Chat (simple)"}
              </Text>
            </HStack>
            <Input
              value={assistantName}
              maxLength={60}
              placeholder="e.g. Acme Dental"
              onChange={(e) => setAssistantName(e.target.value)}
              size="sm"
            />
            <Text fontSize="xs" color="gray.500" alignSelf="flex-end">
              {assistantName.length}/60
            </Text>
            <HStack fontSize="sm" color="gray.600">
              <Text>Model:</Text>
              <Text fontWeight="medium">{llmModel}</Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <HStack
            justify="space-between"
            cursor="pointer"
            onClick={promptDisclosure.onToggle}
          >
            <Text fontWeight="semibold">System prompt (auto-generated)</Text>
            <Icon as={promptDisclosure.isOpen ? MdExpandMore : MdChevronRight} />
          </HStack>
          <Collapse in={promptDisclosure.isOpen}>
            <Textarea
              mt={3}
              rows={14}
              value={compiledPrompt}
              onChange={(e) => setEditedPrompt(e.target.value)}
              fontFamily="mono"
              fontSize="sm"
            />
          </Collapse>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <HStack
            justify="space-between"
            cursor="pointer"
            onClick={advancedDisclosure.onToggle}
          >
            <Text fontWeight="semibold">Advanced</Text>
            <Icon as={advancedDisclosure.isOpen ? MdExpandMore : MdChevronRight} />
          </HStack>
          <Collapse in={advancedDisclosure.isOpen}>
            <VStack spacing={4} align="stretch" mt={4}>
              <FormControl>
                <FormLabel fontSize="sm">LLM model</FormLabel>
                <Select
                  value={llmModel}
                  onChange={(e) => setLlmModel(e.target.value)}
                  size="sm"
                >
                  {LLM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {isVoice && (
                <FormControl>
                  <Checkbox
                    isChecked={useRealtime}
                    onChange={(e) => setUseRealtime(e.target.checked)}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      Use OpenAI Realtime API
                    </Text>
                  </Checkbox>
                  <Text fontSize="xs" color="gray.500" mt={1} ml={6}>
                    Faster turn-taking. Higher cost per minute. Bypasses
                    separate STT / TTS — the model handles both.
                  </Text>
                </FormControl>
              )}
            </VStack>
          </Collapse>
        </CardBody>
      </Card>

      {isVoice && (
        <Card>
          <CardBody>
            <Text fontWeight="semibold" mb={3}>
              Bind a phone number
            </Text>
            {accounts.length === 0 ? (
              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                <Box>
                  No Twilio account found. Add one at{" "}
                  <Button
                    as="a"
                    href="/channels"
                    target="_blank"
                    variant="link"
                    colorScheme="blue"
                    size="sm"
                  >
                    Settings → Channels
                  </Button>{" "}
                  then return here.
                </Box>
              </Alert>
            ) : (
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Twilio account</FormLabel>
                  <Select
                    value={twilioAuthId}
                    placeholder="Pick a Twilio account"
                    onChange={(e) => {
                      setTwilioAuthId(e.target.value);
                      setPhoneSid("");
                    }}
                  >
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isDisabled={!twilioAuthId}>
                  <FormLabel fontSize="sm">Phone number</FormLabel>
                  <Select
                    value={phoneSid}
                    placeholder={
                      twilioAuthId
                        ? phoneNumbers.length === 0
                          ? "No numbers on this account"
                          : "Pick a phone number"
                        : "Pick an account first"
                    }
                    onChange={(e) => setPhoneSid(e.target.value)}
                  >
                    {phoneNumbers.map((p) => (
                      <option key={p.sid} value={p.sid}>
                        {p.friendly_name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            )}
          </CardBody>
        </Card>
      )}

      <Button
        colorScheme="blue"
        size="lg"
        onClick={onClickDeploy}
        isDisabled={deploying || !canDeploy}
      >
        {deploying ? <Spinner size="sm" mr={2} /> : null}
        {deploying ? progress || "Deploying..." : "🚀 Deploy"}
      </Button>
    </VStack>
  );
}
